import React, { Component } from 'react';
import CompetitorPriceChart from './CompetitorPriceChart';
import { navigateTo } from '../utils/navigation';
import { competitorPricesApi, notifyBadgeChanged } from '../utils/competitorPricesApi';
import {
    storeLabel, formatMoney, formatDateTime, offerActions, canAccept, STATE_LABELS, ALERT_REASONS, describeChange,
} from '../utils/competitorSeries';

const RANGES = [['30', '30 días'], ['90', '90 días'], ['365', '1 año'], ['all', 'Todo']];

const ACTIONS = {
    ignore: { label: 'Ignorar', path: 'ignore', title: 'No es el mismo producto (otra edición, una expansión, un combo): deja de contar para la mediana y los avisos.' },
    unignore: { label: 'Dejar de ignorar', path: 'unignore', title: 'Vuelve a contar para la mediana y los avisos.' },
    assign: { label: 'Es de este producto', path: 'assign', title: 'Confirma que esta publicación corresponde a este producto y no a otra edición de la misma ficha.' },
    unassign: { label: 'Deshacer asignación', path: 'unignore', title: 'Vuelve a la atribución automática.' },
};

// El detalle de un producto: cómo estamos parados, la historia de precios por tienda y cada
// publicación de su ficha en el comparador, con lo que se puede hacer sobre ella.
//
// Abrirlo marca como vistos los avisos del producto (lo hace el backend al responder).
class CompetitorPriceDetailModal extends Component {

    constructor(props) {
        super(props);
        this.state = { detail: null, range: '90', loading: true, error: null, busy: null };
    }

    componentDidMount() {
        this.load();
    }

    load = (range = this.state.range) => {
        const { prefix, code } = this.props;
        this.setState({ loading: true, range });
        return competitorPricesApi('GET', `/${encodeURIComponent(prefix)}/${code}?range=${range}`)
            .then((detail) => {
                this.setState({ detail, loading: false, error: null });
                notifyBadgeChanged();
            })
            .catch((error) => this.setState({ loading: false, error: (error && error.message) || 'No se pudo cargar el detalle.' }));
    }

    // Toda acción cambia la posición del producto: se recarga el detalle y se avisa a la lista.
    run = (key, method, path, body) => {
        this.setState({ busy: key });
        return competitorPricesApi(method, path, body)
            .then(() => this.load())
            .then(() => { if (this.props.onChange) this.props.onChange(); })
            .catch((error) => alert((error && error.message) || 'No se pudo completar la acción.'))
            .then(() => this.setState({ busy: null }));
    }

    accept = () => {
        const { prefix, code } = this.props;
        const { position } = this.state.detail;
        const ok = window.confirm(
            `Vas a dejar el precio en ${formatMoney(position.ownPrice)} con la mediana del mercado en ${formatMoney(position.median)}.\n\n`
            + 'El producto sale de "Para revisar" hasta que alguna tienda cambie un precio.'
        );
        if (ok) this.run('accept', 'PUT', `/${encodeURIComponent(prefix)}/${code}/accept`);
    }

    offerAction = (offer, action) => {
        const { prefix, code } = this.props;
        this.run(`${action}-${offer.id}`, 'PUT', `/offers/${offer.id}/${ACTIONS[action].path}`, { prefix, code });
    }

    renderPosition(position) {
        if (position == null) return null;
        let verdict = <span className="badge badge-light">en precio</span>;
        if (position.sinStockPropio) verdict = <span className="badge badge-secondary">sin stock nuestro</span>;
        else if (position.unico) verdict = <span className="badge badge-success">único con stock</span>;
        else if (position.caro) verdict = <span className="badge badge-danger">caro</span>;
        else if (position.barato) verdict = <span className="badge badge-success">barato</span>;
        else if (position.median == null) verdict = <span className="badge badge-light">sin con qué comparar</span>;

        const cell = (label, value) => (
            <div style={{ marginRight: '28px', marginBottom: '8px' }}>
                <div className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase' }}>{label}</div>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>{value}</div>
            </div>
        );
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {cell('Nuestro precio', formatMoney(position.ownPrice))}
                {cell('Mediana', formatMoney(position.median))}
                {cell('Mínimo', formatMoney(position.min))}
                {cell('Tiendas con stock', position.storesInStock)}
                {cell('Diferencia', position.diffPct == null ? '—' : `${position.diffPct > 0 ? '+' : ''}${String(position.diffPct).replace('.', ',')}%`)}
                <div style={{ marginBottom: '12px' }}>
                    {verdict}
                    {position.accepted && <span className="badge badge-light" style={{ marginLeft: '6px' }}>diferencia aceptada</span>}
                </div>
            </div>
        );
    }

    // Qué cambió: los avisos del producto con lo que los provocó. Los que todavía no se habían
    // visto van destacados; los anteriores quedan como historia, más apagados.
    renderAlerts(alerts) {
        if (!alerts || alerts.length === 0) return null;
        return (
            <div style={{ marginBottom: '16px' }}>
                {alerts.map((alert) => (
                    <div
                        key={alert.id}
                        className={`alert ${alert.seen ? 'alert-secondary' : 'alert-danger'} mb-2`}
                        style={{ fontSize: '13px' }}
                    >
                        <strong>{ALERT_REASONS[alert.reason] || alert.reason}</strong>
                        {' '}en la lectura del {formatDateTime(alert.at)}
                        {alert.reason === 'caro' && alert.median != null && (
                            <> — nuestro precio {formatMoney(alert.ownPrice)}, la mediana quedó en {formatMoney(alert.median)}</>
                        )}
                        {alert.changes && alert.changes.length > 0 && (
                            <ul className="mb-0" style={{ paddingLeft: '18px', marginTop: '4px' }}>
                                {alert.changes.map((change, index) => <li key={index}>{describeChange(change)}</li>)}
                            </ul>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    renderRecentChanges(changes) {
        if (!changes || changes.length === 0) return null;
        return (
            <>
                <h6 className="mb-2" style={{ marginTop: '18px' }}>Últimos cambios en las otras tiendas</h6>
                <table className="table table-sm mb-0">
                    <tbody>
                        {changes.map((change, index) => (
                            <tr key={index}>
                                <td className="text-muted" style={{ whiteSpace: 'nowrap', width: '1%' }}>{formatDateTime(change.at)}</td>
                                <td>{describeChange(change)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </>
        );
    }

    renderOffers(offers) {
        if (!offers || offers.length === 0) {
            return <div className="text-muted">El comparador no lista otras tiendas para este producto.</div>;
        }
        return (
            <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm mb-0">
                    <thead className="bg-light">
                        <tr>
                            <th className="border-0">Tienda</th>
                            <th className="border-0" style={{ textAlign: 'right' }}>Precio</th>
                            <th className="border-0">Stock</th>
                            <th className="border-0">Condición</th>
                            <th className="border-0">Vista</th>
                            <th className="border-0" style={{ textAlign: 'right' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {offers.map((offer) => (
                            <tr key={offer.id} style={{ opacity: offer.state === 'confirmed' ? 1 : 0.65 }}>
                                <td>
                                    <a href={offer.url} target="_blank" rel="noopener noreferrer">{storeLabel(offer.store)}</a>
                                    {offer.title && <div style={{ fontSize: '11px' }} className="text-muted">{offer.title}</div>}
                                </td>
                                <td style={{ textAlign: 'right' }}>{formatMoney(offer.price)}</td>
                                <td>{!offer.present ? 'ya no figura' : (offer.inStock ? 'con stock' : 'sin stock')}</td>
                                <td>{STATE_LABELS[offer.state] || offer.state}</td>
                                <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(offer.lastSeenAt)}</td>
                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    {offerActions(offer).map((action) => (
                                        <button
                                            key={action}
                                            type="button"
                                            className={`btn btn-sm ${action === 'assign' ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
                                            style={{ marginLeft: '6px' }}
                                            title={ACTIONS[action].title}
                                            disabled={this.state.busy != null}
                                            onClick={() => this.offerAction(offer, action)}
                                        >
                                            {ACTIONS[action].label}
                                        </button>
                                    ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { detail, loading, error, range } = this.state;
        const title = detail && detail.product && detail.product.name ? detail.product.name : 'Producto';

        return (
            <>
                <div className="modal fade show" style={{ display: 'block', overflowY: 'auto' }} role="dialog">
                    <div className="modal-dialog modal-lg" role="document" style={{ maxWidth: '960px' }}>
                        <div className="modal-content" style={{ borderRadius: '0.75rem' }}>
                            <div className="modal-header">
                                <div>
                                    <h4 className="modal-title mb-1">{title}</h4>
                                    {detail && detail.product.fichaName && (
                                        <small className="text-muted">En el comparador: {detail.product.fichaName}</small>
                                    )}
                                </div>
                                <button type="button" className="close" aria-label="Close" onClick={this.props.onClose}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                {error && <div className="alert alert-warning">{error}</div>}
                                {!detail && loading && (
                                    <div className="containerLoader"><div className="loader"></div></div>
                                )}
                                {detail && (
                                    <>
                                        {this.renderAlerts(detail.alerts)}
                                        {this.renderPosition(detail.position)}

                                        <div style={{ margin: '4px 0 16px' }}>
                                            {canAccept(detail.position) && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    style={{ marginRight: '6px' }}
                                                    disabled={this.state.busy != null}
                                                    title="Dejar el precio como está. Si alguna tienda cambia el suyo, vuelve a aparecer."
                                                    onClick={this.accept}
                                                >
                                                    Está bien así
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => navigateTo(`/editProduct?code=${detail.product.code}`)}
                                            >
                                                Editar producto
                                            </button>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                                            <h6 className="mb-2">Historia de precios</h6>
                                            <div className="btn-group btn-group-sm mb-2" role="group">
                                                {RANGES.map(([value, label]) => (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        className={`btn ${range === value ? 'btn-primary' : 'btn-white'}`}
                                                        onClick={() => this.load(value)}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <CompetitorPriceChart series={detail.series} />
                                        <div className="text-muted" style={{ fontSize: '11px', margin: '4px 0 18px' }}>
                                            Punteado: la tienda no tenía stock. Más claro: historia anterior a nuestras lecturas, tomada del comparador.
                                        </div>

                                        <h6 className="mb-2">Publicaciones en otras tiendas</h6>
                                        {this.renderOffers(detail.offers)}
                                        {this.renderRecentChanges(detail.recentChanges)}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal-backdrop fade show"></div>
            </>
        );
    }
}

export default CompetitorPriceDetailModal;
